/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useEffect, useRef, useState } from 'react'
import { LDProvider } from 'launchdarkly-react-client-sdk'

import Feature from '@modules/utils/feature'
import {
  makeMultiLDContextWithResource,
  makeUserLDContext,
  useIdentify,
} from '@modules/launchdarkly'
import type { ResourceContext } from '@modules/launchdarkly'
import { LaunchDarklyProvider } from '@modules/launchdarkly/LaunchDarklyProvider'
import { useProfile } from '@modules/profile-lib/hooks'
import type { MiddlewareComponent } from '@modules/app/types'
import ProfileProvider from '@modules/profile-lib/ProfileProvider'

// eslint-disable-next-line import/no-restricted-paths
import { getConfigForKey } from '@/store'

const LaunchDarklyMiddleware: MiddlewareComponent = ({ children }) => {
  const launchDarklyId = getConfigForKey(Feature.launchdarklyId)
  const launchDarklyClientInitializationTimeoutSeconds =
    getConfigForKey('LAUNCHDARKLY_CLIENT_INITIALIZATION_TIMEOUT_SECONDS') ?? 5

  if (!launchDarklyId) {
    return <Fragment>{children}</Fragment>
  }

  return (
    <LDProvider
      clientSideID={launchDarklyId}
      timeout={launchDarklyClientInitializationTimeoutSeconds}
    >
      <LaunchDarklyProvider>{children}</LaunchDarklyProvider>
    </LDProvider>
  )
}

LaunchDarklyMiddleware.middleware = {
  displayName: 'LaunchDarklyMiddleware',
  dependencies: [ProfileProvider],
}

export default LaunchDarklyMiddleware

type Props = {
  resource: ResourceContext
}

const LDResourceContextUpdater = ({ resource }: Props) => {
  const resourceKey = useRef('')
  const profile = useProfile()
  const [identified, setIdentified] = useState<boolean>(false)
  const [identify, status] = useIdentify()

  useEffect(() => {
    async function updateContext() {
      const { isIdentifying, clientInitialized } = status

      if (isIdentifying || !clientInitialized || identified) {
        return
      }

      if (!profile) {
        return
      }

      if (resourceKey.current === resource.key) {
        return
      }

      resourceKey.current = resource.key
      const newContext = makeMultiLDContextWithResource(profile, resource)

      try {
        await identify(newContext)
        setIdentified(true)
      } catch (error) {
        console.error('Error updating LaunchDarkly context:', error)
      }
    }

    updateContext()
  }, [status, profile, resource, identified, identify])

  useEffect(
    () => () => {
      const revertContext = async () => {
        if (!profile || !identified) {
          return
        }

        await identify(makeUserLDContext(profile))
      }

      revertContext()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return null
}

export { LDResourceContextUpdater }
